// components/conference/ConferenceDetails.tsx
'use client';

import { useState } from 'react';
import styles from './ConferenceDetails.module.scss';
import {
  Target,
  Users,
  BarChart3,
  MessageSquare,
  FileText,
  Scale,
  FileCheck,
  Award,
  Calendar,
  Clock,
  Coffee,
  ChevronDown,
  Sparkles,
  Zap,
  Gift,
  Users2,
  Star, Cpu,
} from 'lucide-react';
import { useRouter } from 'next/navigation';

const goals = [
  {
    icon: Target,
    title: 'Обмен актуальной информацией',
    description: 'Рассматриваются изменения в законодательстве и практика госзакупок в 2026 году',
    tag: 'Тренды 2026'
  },
  {
    icon: Users,
    title: 'Нетворкинг',
    description: 'Возможность познакомиться с коллегами для обмена опытом',
    tag: 'Профессиональные связи'
  },
  {
    icon: BarChart3,
    title: 'Практические примеры',
    description: 'Разбор ситуаций из практики и возможных решений',
    tag: 'Примеры'
  },
  {
    icon: MessageSquare,
    title: 'Диалог с экспертами',
    description: 'Возможность задать вопросы специалистам-практикам',
    tag: 'Живое общение'
  }
];

const keyThemes = [
  {
    icon: FileText,
    title: 'Единственный поставщик',
    description: 'Особенности и условия закупки у единственного поставщика в 2026 году',
    tag: 'Актуально'
  },
  {
    icon: Scale,
    title: 'Национальный режим',
    description: 'Вопросы применения национального режима при осуществлении закупок',
    tag: 'Важно'
  },
  {
    icon: FileCheck,
    title: 'Исполнение договоров',
    description: 'Изменения, расторжения, претензионная работа',
    tag: 'Практика'
  },
  {
    icon: Cpu,
    title: 'Искусственный интеллект в закупках',
    description: 'Это возможно: новый уровень эффективности',
    tag: 'Технологии'
  }
];

const agendaSections = [
  {
    time: '10:30 - 11:00',
    title: 'Регистрация',
    items: [
      'Регистрация гостей',
      'Приветственный кофе'
    ]
  },
  {
    time: '11:00 - 12:30',
    title: 'КВИЗ',
    items: [
      'Квиз с разбором вопросов от экспертов',
      'Интерактивная командная игра'
    ]
  },
  {
    time: '12:30 - 13:00',
    title: 'Дискуссия',
    items: [
      'Дискуссия с экспертами',
      'Подведение итогов'
    ]
  }
];

const faqItems = [
  {
    question: 'Какой формат участия?',
    answer: 'Мероприятие проводится в очном формате. Это позволит вам максимально погрузиться в атмосферу живого общения, поучаствовать в командном квизе и лично пообщаться с экспертами и коллегами.'
  },
  {
    question: 'Можно ли участвовать с коллегами?',
    answer: 'Да, мы предусмотрели как командное, так и индивидуальное участие. Вы можете прийти всей командой или самостоятельно — в любом случае вас ждет увлекательная игра и новые знакомства.'
  },
  {
    question: 'Кто может участвовать?',
    answer: 'ВПЕРВЫЕ мероприятие объединяет и Заказчиков, и Поставщиков! Это уникальная возможность для диалога между двумя сторонами закупочного процесса.'
  },
  {
    question: 'Сколько стоит участие?',
    answer: 'УЧАСТИЕ БЕСПЛАТНО! Мы рады пригласить всех желающих на наш первый КВИЗ, посвященный госзакупкам.'
  },
  {
    question: 'Как зарегистрироваться на квиз?',
    answer: 'Для регистрации заполните форму на этой странице. Количество мест ограничено, поэтому рекомендуем забронировать место заранее.'
  }
];

export default function ConferenceDetails() {
  const router = useRouter();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();

    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      window.history.pushState(null, '', `#${targetId}`);
    } else {
      router.push(`#${targetId}`);
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* О конференции + Ключевые темы */}
        <div className={styles.goalsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              О <span className={styles.highlight}>КВИЗе</span>
            </h2>
            <p className={styles.sectionSubtitle}>
              Интерактивный формат для профессионалов в закупках
            </p>
          </div>

          <div className={styles.goalsGrid}>
            {goals.map((goal, index) => {
              const Icon = goal.icon;
              return (
                <div key={index} className={styles.goalCard}>
                  <div className={styles.goalIcon}>
                    <Icon size={28} />
                  </div>
                  <h3 className={styles.goalTitle}>{goal.title}</h3>
                  <p className={styles.goalDescription}>{goal.description}</p>
                  {goal.tag && (
                    <span className={styles.goalTag}>
                      {goal.tag}
                    </span>
                  )}
                </div>
              );
            })}

            {/* Ключевые темы (3 карточки) */}
            {keyThemes.map((theme, index) => {
              const Icon = theme.icon;
              return (
                <div key={`theme-${index}`} className={`${styles.goalCard} ${styles.themeCard}`}>
                  <div className={styles.goalIcon}>
                    <Icon size={28} />
                  </div>
                  <h3 className={styles.goalTitle}>{theme.title}</h3>
                  <p className={styles.goalDescription}>{theme.description}</p>
                  {theme.tag && (
                    <span className={styles.goalTag}>
                      {theme.tag}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Программа КВИЗа */}
        <div className={styles.agendaSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              Программа <span className={styles.highlight}>КВИЗа</span>
            </h2>
            <p className={styles.sectionSubtitle}>
              Расписание мероприятия
            </p>
          </div>

          <div className={styles.agendaGrid}>
            {agendaSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className={styles.agendaCard}>
                <div className={styles.agendaHeader}>
                  <div className={styles.agendaTime}>
                    <Clock size={20} />
                    <span>{section.time}</span>
                  </div>
                </div>

                <div className={styles.agendaContent}>
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#0f172a',
                    marginBottom: '16px'
                  }}>
                    {section.title}
                  </h4>
                  <ul className={styles.agendaList}>
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className={styles.agendaItem}>
                        <div className={styles.itemDot}></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className={styles.faqSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              Частые <span className={styles.highlight}>вопросы</span>
            </h2>
            <p className={styles.sectionSubtitle}>
              Ответы на вопросы об участии в КВИЗе
            </p>
          </div>

          <div className={styles.faqList}>
            {faqItems.map((item, index) => (
              <div
                key={index}
                className={`${styles.faqItem} ${openFaqIndex === index ? styles.open : ''}`}
                onClick={() => toggleFaq(index)}
              >
                <div className={styles.faqQuestion}>
                  <h3 className={styles.questionText}>{item.question}</h3>
                  <div className={styles.chevron}>
                    <ChevronDown size={20} />
                  </div>
                </div>

                <div className={`${styles.faqAnswer} ${openFaqIndex === index ? styles.visible : ''}`}>
                  <div className={styles.answerContent}>
                    <p style={{ paddingTop: 16 }}>{item.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Бейдж бесплатного участия */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className={styles.freeBadge}>
              <Gift />
              <span>УЧАСТИЕ БЕСПЛАТНО</span>
            </div>
          </div>
        </div>

        {/* CTA секция */}
        <div className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h3 className={styles.ctaTitle}>
              Присоединиться к КВИЗу
            </h3>

            <p className={styles.ctaDescription}>
              Забронируйте место для участия
            </p>

            <div className={styles.ctaButtons}>
              <a
                href="#form"
                className={styles.primaryButton}
                onClick={(e) => handleScroll(e, 'form')}
                aria-label="Забронировать место на КВИЗе"
              >
                Забронировать место
              </a>
            </div>

            <div className={styles.ctaNote}>
              <Users2 size={18} />
              <span>Очный формат • Командная игра • Живое общение</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}